import { ReactNode, useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { ChevronDown, X } from 'lucide-react';
import LoadingDots from '../LoadingDots';

export interface Option {
  value: string | number;
  label: string;
  icon?: ReactNode;
}

interface SelectProps {
  options: Option[];
  defaultOption?: string | number;
  onChange?: (selected: Option | undefined) => void;
  onSearch?: (text: string) => void;
  label?: string;
  disabled?: boolean;
  backgroundColor?: string;
  infoModal?: ReactNode;
  clearOnSelect?: boolean;
  placeholder?: string;
  dropdown?: boolean;
  loading?: boolean;
  validation?: (text: string) => boolean;
  onValidationFail?: (text: string) => void;
}

export const Searchable = ({
  options,
  label,
  defaultOption,
  onChange,
  disabled = false,
  backgroundColor = 'bg-zinc-900',
  infoModal,
  onSearch,
  clearOnSelect = false,
  placeholder = 'Search...',
  dropdown = true,
  loading = false,
  validation,
  onValidationFail,
}: SelectProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [selectedOption, setSelectedOption] = useState(
    defaultOption ? options.find((opt) => opt.value === defaultOption) || options[0] : options[0]
  );
  const [text, setText] = useState(selectedOption?.label || '');
  const [isOpen, setIsOpen] = useState(false);
  const _options = useMemo(() => [...options] as Option[], [options]);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const listRef = useRef<HTMLUListElement>(null);

  const optionsString = useMemo(() => JSON.stringify(_options), [_options]);

  const handleSelect = useCallback(
    (option: Option) => {
      setSelectedOption(option);
      setIsOpen(false);
      inputRef.current?.blur();
      if (validation && !validation(text)) {
        onValidationFail?.(text);
        return setText('');
      }
      if (onChange) {
        onChange({ value: option.value, label: option.label, icon: option.icon });
      }
      if (clearOnSelect) {
        setText('');
      } else {
        setText(option.label);
      }
    },
    [validation, onValidationFail, text, onChange, clearOnSelect]
  );

  useEffect(() => {
    if (onSearch) {
      onSearch(text);
    }
    if (text === '' && onChange) {
      onChange(undefined);
    }
    // check if the text is not in the options
    if (onChange && !!text && !options.find((option) => option.label === text)) {
      onChange(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setText('');
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    let input = inputRef.current;
    const listener = (e: KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'Enter':
          e.preventDefault();
          if (activeIndex >= 0 && activeIndex < _options.length) {
            handleSelect(_options[activeIndex]);
          } else if (_options.length > 0) {
            handleSelect(_options[0]);
          }
          break;
        case 'ArrowDown':
          e.preventDefault();
          if (activeIndex < _options.length - 1) {
            setActiveIndex((prev) => prev + 1);
          }
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (activeIndex > 0) {
            setActiveIndex((prev) => prev - 1);
          }
          break;
        case 'Escape':
          setIsOpen(false);
          inputRef.current?.blur();
          break;
        case 'Tab':
          setIsOpen(false);
          break;
      }
    };

    if (input) {
      input.addEventListener('keydown', listener);
    }

    return () => {
      if (input) {
        input.removeEventListener('keydown', listener);
      }
    };
  }, [_options, isOpen, activeIndex, handleSelect]);

  useEffect(() => {
    setActiveIndex(-1);
  }, [optionsString]);

  useEffect(() => {
    if (!isOpen || activeIndex < 0 || !listRef.current) return;

    const activeElement = document.getElementById(`option-${activeIndex}`);
    if (!activeElement) return;

    const listElement = listRef.current;
    const activeRect = activeElement.getBoundingClientRect();
    const listRect = listElement.getBoundingClientRect();

    if (activeRect.bottom > listRect.bottom) {
      listElement.scrollTop += activeRect.bottom - listRect.bottom;
    } else if (activeRect.top < listRect.top) {
      listElement.scrollTop -= listRect.top - activeRect.top;
    }
  }, [activeIndex, isOpen]);

  return (
    <div ref={containerRef} className={'inline-flex flex-1 flex-col gap-1 w-full'}>
      <div className={'flex justify-between items-end'}>
        {label && <span className="text-sm text-zinc-400">{label}</span>}
        {!!infoModal && infoModal}
      </div>
      <div className="relative flex-1">
        <div
          className={`flex items-center justify-between w-full px-4 py-3 text-md border border-zinc-700 rounded-md bg-transparent focus:outline-none ${disabled ? 'touch-none cursor-not-allowed' : ''}`}
        >
          <input
            ref={inputRef}
            type="text"
            className="w-full bg-transparent outline-none"
            placeholder={placeholder || 'Search...'}
            value={text}
            onChange={(e) => {
              if (e.target.value !== text) {
                setText(e.target.value);
              }
            }}
            onFocus={() => {
              setIsOpen(true);
              setIsFocused(true);
            }}
            onBlur={(e) => {
              if (!e.relatedTarget || !listRef.current?.contains(e.relatedTarget)) {
                setIsFocused(false);
              }
            }}
            role="combobox"
            aria-expanded={isOpen}
            aria-controls="options-listbox"
            aria-activedescendant={activeIndex >= 0 ? `option-${activeIndex}` : undefined}
          />
          {loading && (
            <div className="flex items-center justify-center mr-2">
              <LoadingDots />
            </div>
          )}
          {!!text && (
            <button
              onClick={() => {
                inputRef.current?.focus();
                setText('');
              }}
              className="text-zinc-300"
            >
              <X className="flex shrink-0 w-5 h-5" />
            </button>
          )}
          {dropdown && (
            <>
              <div className="h-6 w-[1px] bg-zinc-700 mx-2" />
              <button
                onClick={() => {
                  if (isOpen) {
                    setIsOpen(false);
                  } else {
                    inputRef.current?.focus();
                    if (onSearch) {
                      onSearch('');
                    }
                  }
                }}
                className="flex shrink-0 cursor-pointer"
              >
                <ChevronDown size={24} />
              </button>
            </>
          )}
        </div>
        {isOpen && _options.length > 0 && (
          <div className={`absolute z-10 w-full mt-1 ${backgroundColor} rounded-md shadow-lg`}>
            <ul
              ref={listRef}
              role="listbox"
              id="options-listbox"
              className="py-1 overflow-auto text-base rounded-md max-h-48 focus:outline-none border border-zinc-700"
              aria-label="Options"
            >
              {_options.map((option, index) => (
                <li
                  key={option.value}
                  id={`option-${index}`}
                  role="option"
                  aria-selected={activeIndex === index}
                  className={`flex flex-row items-center truncate cursor-default select-none relative py-2 pl-3 pr-3 ${
                    activeIndex === index ? 'bg-zinc-800' : 'hover:bg-zinc-800'
                  }`}
                  onClick={() => handleSelect(option)}
                  onMouseEnter={() => setActiveIndex(index)}
                  tabIndex={-1}
                >
                  {option.icon && <span className="flex flex-shrink-0 mr-2">{option.icon}</span>}
                  <span className="truncate">{option.label}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};
